/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';

import { LoopAttachmentButton, LoopAttachmentList } from '../pages/loops/LoopAttachments';
import type { LoopAttachmentsState } from '../pages/loops/LoopAttachments';
import { RecordingToggle, useLoopCreationOptions } from '../pages/loops/LoopCreationOptions';
import { LoopWatermarkToggle } from '../pages/loops/LoopWatermarkToggle';
import type { LoopLaunchOptions } from '../services/landingSkillRouting';
import { loopService } from '../services/loopService';
import type { BrowserProfile } from '../services/loopService';

import { BrowserProfilePicker } from './BrowserProfilePicker';

import './LandingLoopControls.css';

const useLandingProfiles = (authenticated: boolean) => {
  const [profiles, setProfiles] = useState<BrowserProfile[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!authenticated) return undefined;
    const controller = new globalThis.AbortController();
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const result = await loopService.profiles({ signal: controller.signal });

        if (!controller.signal.aborted) { setProfiles(result.filter((profile) => profile.status !== 'revoked')); setError(false); }
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally { pending = false; }
    };

    void refresh();
    const timer = window.setInterval(() => void refresh(), 15000);
    const onFocus = () => void refresh();

    window.addEventListener('focus', onFocus);

    return () => { controller.abort(); window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [authenticated]);

  return { profiles, error };
};

export const LandingLoopControls: React.FC<{
  authenticated: boolean; disabled?: boolean; onChange: (options: LoopLaunchOptions) => void;
  attachments?: LoopAttachmentsState; onSignIn?: () => void;
}> = ({ authenticated, disabled, onChange, attachments, onSignIn }) => {
  const { profiles, error } = useLandingProfiles(authenticated);
  const { profileId, setProfileId, recordVideo, setRecordVideo,
    watermarkEnabled, setWatermarkEnabled } = useLoopCreationOptions(profiles);
  const attachmentKey = attachments?.attachmentIds.join(',') || '';
  const notice = !authenticated ? 'Sign in to choose a connected browser.'
    : error ? 'Browser profiles could not be loaded. Checking again shortly.' : '';

  useEffect(() => { onChange({ browserProfileId: profileId || undefined, recordVideo, watermarkEnabled,
    attachmentIds: attachmentKey ? attachmentKey.split(',') : [] }); },
  [onChange, profileId, recordVideo, watermarkEnabled, attachmentKey]);

  return <div className="landing-loop-controls">
    {attachments && <LoopAttachmentButton state={attachments} disabled={disabled}
      onSignIn={!authenticated ? onSignIn : undefined} />}
    <BrowserProfilePicker profiles={profiles} value={profileId} onChange={setProfileId}
      disabled={disabled || !authenticated} compact />
    <div className="loop-recording-controls">
      <RecordingToggle value={recordVideo} onChange={setRecordVideo} disabled={disabled} compact />
      <LoopWatermarkToggle value={watermarkEnabled} onChange={setWatermarkEnabled} disabled={disabled} />
    </div>
    {notice && <span className="landing-loop-controls__notice" role="status" title={notice} aria-label={notice}>
      <i className="fa-solid fa-circle-info" aria-hidden="true" /></span>}
    {attachments && <LoopAttachmentList state={attachments} disabled={disabled} />}
  </div>;
};
